import React, { useState } from "react";
import { Plus } from "lucide-react";
import Reward from "./Reward";
import {
  Reward as RewardType,
  StarHistoryEntry,
  ThemeName,
} from "@/utils/storage";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle } from "@/components/ui/card";
import { DeleteConfirmationDialog } from "../dialogs";

interface RewardListProps {
  rewards: RewardType[];
  availableStars: number;
  totalStarsEarned?: number;
  editMode: boolean;
  isAnimating?: boolean;
  starHistory?: StarHistoryEntry[];
  theme?: ThemeName;
  onClaim: (reward: RewardType) => void;
  onSpendMoney?: (reward: RewardType, amount: number) => void;
  onEdit?: (reward: RewardType) => void;
  onDelete?: (id: string) => void;
  onMoveUp?: (id: string) => void;
  onMoveDown?: (id: string) => void;
  onAddNew: () => void;
}

const RewardList: React.FC<RewardListProps> = ({
  rewards,
  availableStars,
  totalStarsEarned = 0,
  editMode,
  isAnimating,
  starHistory = [],
  theme = "robots",
  onClaim,
  onSpendMoney,
  onEdit,
  onDelete,
  onMoveUp,
  onMoveDown,
  onAddNew,
}) => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [rewardToDelete, setRewardToDelete] = useState<RewardType | null>(null);

  const handleClaimReward = (reward: RewardType) => {
    if (!isAnimating) {
      onClaim(reward);
    }
  };

  const handleSpendMoney = (reward: RewardType, amount: number) => {
    if (!isAnimating && onSpendMoney) {
      onSpendMoney(reward, amount);
    }
  };

  const handleDeleteClick = (reward: RewardType) => {
    setRewardToDelete(reward);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (rewardToDelete && onDelete) {
      onDelete(rewardToDelete.id);
    }
    setDeleteDialogOpen(false);
    setRewardToDelete(null);
  };

  return (
    <>
      {/* Header with available stars */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">🎁 Rewards</div>
            <div className="flex items-center gap-4">
              <div className="text-sm font-normal text-muted-foreground">
                {availableStars}
              </div>
              {editMode && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onAddNew}
                  className="flex items-center gap-1"
                >
                  <Plus size={16} />
                  Add Reward
                </Button>
              )}
            </div>
          </CardTitle>
        </CardHeader>
      </Card>

      <div className="space-y-4">
        {rewards.length === 0 ? (
          <div className="text-center p-8 glassmorphism rounded-2xl">
            <p className="text-muted-foreground">No rewards yet 😢</p>
            {editMode && (
              <Button
                variant="outline"
                size="sm"
                onClick={onAddNew}
                className="mt-2"
              >
                Add your first reward 🎉
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {rewards.map((reward) => (
              <Reward
                key={reward.id}
                reward={reward}
                availableStars={availableStars}
                totalStarsEarned={totalStarsEarned}
                onClaim={handleClaimReward}
                onSpendMoney={handleSpendMoney}
                onEdit={editMode ? onEdit : undefined}
                onDelete={
                  editMode ? () => handleDeleteClick(reward) : undefined
                }
                onMoveUp={editMode ? onMoveUp : undefined}
                onMoveDown={editMode ? onMoveDown : undefined}
                editMode={editMode}
              />
            ))}
          </div>
        )}
      </div>

      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleConfirmDelete}
        itemType="reward"
        itemName={rewardToDelete?.title}
      />
    </>
  );
};

export default RewardList;
